<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { Container } from '../../../../../components/Container';
	import { NAVBAR_SCROLL_MARGIN } from '../../../../navigation/constants/sizing';
	import type { FilterSections, FilterSortOption } from '../../../types';
	import Filter from '../Filter.svelte';
	import FilterResultsRow from '../FilterResultsRow/FilterResultsRow.svelte';

	interface Props {
		dataCy: string;
		filterSections: FilterSections;
		sorts?: FilterSortOption[][];
		count?: number | null;
		suffix?: string;
		loading?: boolean;
		withMaxSectionHeight?: boolean;
		pageParams?: string[];
		trackPrefix?: string;
		title?: import('svelte').Snippet;
		class?: string;
		filterClass?: string;
		resultsClass?: string;
		/**
		 * Scroll margin for the results-start anchor. Must match the toolbar's
		 * sticky `top`, otherwise `scrollToFilterResults` lands the anchor above
		 * the pinned toolbar and the first row of results ends up hidden behind
		 * it. Override alongside any `class` that changes `top`. Defaults to
		 * clearing the global bar, plus the platform sub-nav from `lg`.
		 */
		scrollMarginClass?: string;
	}

	let {
		dataCy,
		filterSections,
		sorts = undefined,
		count = undefined,
		suffix = '',
		loading = false,
		withMaxSectionHeight = true,
		pageParams = [],
		trackPrefix = undefined,
		title = undefined,
		filterClass = undefined,
		resultsClass = undefined,
		scrollMarginClass = NAVBAR_SCROLL_MARGIN,
		...rest
	}: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-sticky`);
	let targetId = $derived(`${dataCy}-filter-results-start`);
	let scrollTarget = $derived(`#${targetId}`);
</script>

{#if title}
	<!-- On mobile the title sits above the sticky bar (and its results-start
	     anchor) so it scrolls away with the page rather than needing to be hidden
	     once the bar pins. On desktop it lives inline in the toolbar row below. -->
	<Container dataCy={`${dataCyPrefix}-title`} class="mb-2 sm:hidden">
		{@render title()}
	</Container>
{/if}

<span id={targetId} class={twMerge('block', scrollMarginClass)} aria-hidden="true"></span>

<!-- The toolbar and the applied-filter chips row stick together as one unit.
     Keeping them in a single sticky wrapper (rather than giving each its own
     `top`) means the chips row pins directly beneath the toolbar regardless of
     the toolbar's height, which varies across breakpoints. -->
<div
	class={twMerge('sticky-filter-toolbar sticky top-12 z-[11] bg-white lg:top-[6.5rem]', rest.class)}
>
	<Container
		class={twMerge(
			'flex min-h-14 flex-col-reverse items-center justify-between gap-2 py-2 sm:flex-row sm:items-end sm:gap-4'
		)}
		dataCy={`${dataCy}-filters`}
	>
		{#if title}
			<div class="hidden sm:order-first sm:block sm:w-auto">
				{@render title()}
			</div>
		{/if}
		<!-- `filterSections` is passed for the zero-results event only: this row
		     owns the count, but needs the applied facets to report which
		     combination matched nothing. Chips are rendered by the row below. -->
		<FilterResultsRow
			{count}
			{loading}
			{suffix}
			{filterSections}
			hideChips
			class="w-full min-w-0 sm:w-auto sm:flex-1 sm:self-center"
		/>
		<Filter
			{filterSections}
			{sorts}
			{withMaxSectionHeight}
			{pageParams}
			{scrollTarget}
			{trackPrefix}
			compact
			class={twMerge('w-full sm:ml-auto sm:w-auto', filterClass)}
		/>
	</Container>

	<Container dataCy={`${dataCy}-separator`}>
		<hr class="border-gray-100" />
	</Container>

	<Container dataCy={`${dataCyPrefix}-filter-row`} class={twMerge('mt-2', resultsClass)}>
		<FilterResultsRow count={undefined} {filterSections} {scrollTarget} />
	</Container>
</div>

<style>
	/* Keep the fixed drawer above site chrome while its sticky ancestor creates a stacking context. */
	.sticky-filter-toolbar:has(:global([role='dialog'])) {
		z-index: 60;
	}
</style>
